import { createFileRoute, redirect } from "@tanstack/react-router";
import { Ban, Bell, Image, Key, List, Trash2, User } from "lucide-react";
import { usePublicConfig } from "@/api/queries/config";
import {
	AccountDeletion,
	BlockedUsersList,
	NotificationSettings,
	PasswordChangeForm,
	ProfileAttributesForm,
	ProfileImagesManager,
	ProfileInfoForm,
} from "@/components/aboutme";
import { Tabs } from "@/components/ui/Tabs";
import { usePageMeta } from "@/hooks/use-page-meta";

const TAB_VALUES = [
	"profile",
	"images",
	"attributes",
	"password",
	"notifications",
	"account",
	"blocked",
] as const;

type TabValue = (typeof TAB_VALUES)[number];

const tabs = [
	{ key: "profile", label: "Profil", icon: <User size={16} /> },
	{ key: "images", label: "Bilder", icon: <Image size={16} /> },
	{ key: "attributes", label: "Eigenschaften", icon: <List size={16} /> },
	{ key: "password", label: "Passwort", icon: <Key size={16} /> },
	{ key: "notifications", label: "Benachrichtigungen", icon: <Bell size={16} /> },
	{ key: "account", label: "Konto", icon: <Trash2 size={16} /> },
	{ key: "blocked", label: "Blockiert", icon: <Ban size={16} /> },
] as const;

function isValidTab(tab: string): tab is TabValue {
	return (TAB_VALUES as readonly string[]).includes(tab);
}

export const Route = createFileRoute("/_app/aboutme/$tab")({
	beforeLoad: ({ params }) => {
		if (!isValidTab(params.tab)) {
			throw redirect({ to: "/aboutme/$tab", params: { tab: "profile" } });
		}
	},
	component: AboutMeTabPage,
});

function AboutMeTabPage() {
	const { tab } = Route.useParams();
	const navigate = Route.useNavigate();
	const { data: config } = usePublicConfig();

	usePageMeta({
		title: config ? `Mein Profil - ${config.seo.defaultTitle}` : undefined,
	});

	const activeTab = tab as TabValue;

	function handleTabChange(key: string) {
		navigate({ to: "/aboutme/$tab", params: { tab: key } });
	}

	return (
		<div className="space-y-6">
			<h1 className="text-2xl font-bold text-text">Mein Profil</h1>

			<Tabs tabs={[...tabs]} activeKey={activeTab} onTabChange={handleTabChange} />

			<div>
				{activeTab === "profile" && <ProfileInfoForm />}
				{activeTab === "images" && <ProfileImagesManager />}
				{activeTab === "attributes" && <ProfileAttributesForm />}
				{activeTab === "password" && <PasswordChangeForm />}
				{activeTab === "notifications" && <NotificationSettings />}
				{activeTab === "account" && <AccountDeletion />}
				{activeTab === "blocked" && <BlockedUsersList />}
			</div>
		</div>
	);
}
